import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import LoadingSpinner from "./components/LoadingSpinner";
import ToastProvider from "./components/ToastProvider";
import ProtectedRoute from "./components/ProtectedRoute";
import AppFooter from "./components/AppFooter";
import { isElectron } from "./utils/electronDownload";

// reCAPTCHA does not work in Electron (file:// or app protocol has invalid origin for Google).
// Disable it so login uses backend "bypass" and does not try to load the script.
const recaptchaSiteKey = isElectron() ? "" : (process.env.REACT_APP_RECAPTCHA_SITE_KEY || "");

const Login = lazy(() => import("./pages/auth/Login"));
const QADashboard = lazy(() => import("./pages/qa/QADashboard"));
const QAInstructionsPage = lazy(() => import("./pages/qa/QAInstructionsPage"));
const DocumentEditPage = lazy(() => import("./pages/qa/DocumentEditPage"));
/**
 * QA-only SPA (trans.*).
 *
 * Admin/Superadmin is now a separate deploy at admin.* (see `/admin-portal`).
 * Doctor UI remains a separate deploy at doctor.* (see `/doctor-portal`).
 */
const QAPortalRoutes = () => (
  <>
    <Helmet>
      <title>ScribeInAI Medical Transcription</title>
      <meta name="description" content="ScribeInAI Medical Transcription" />
    </Helmet>
    <Suspense fallback={<LoadingSpinner fullScreen text="Loading application..." />}>
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/login" element={<Login />} />
        <Route
          path="/qa-instructions"
          element={
            <ProtectedRoute allowQaTrack>
              <QAInstructionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/qa-dashboard"
          element={
            <ProtectedRoute allowQaTrack>
              <QADashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/qa-dashboard/document-edit"
          element={
            <ProtectedRoute allowQaTrack>
              <DocumentEditPage />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  </>
);

export default function App() {
  const showMainFooter = true;
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <GoogleReCaptchaProvider
        reCaptchaKey={recaptchaSiteKey}
        useEnterprise={false}
        scriptProps={{ async: true, defer: true }}
      >
        <ToastProvider>
          <div className="min-h-screen flex flex-col">
            <main className="flex-1 pb-12">
              <QAPortalRoutes />
            </main>
            {showMainFooter && <AppFooter />}
          </div>
        </ToastProvider>
      </GoogleReCaptchaProvider>
    </Router>
  );
}
